<!-- 
Dialog for configuring the invoice timing for a levy collection.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Configure invoice timing</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="configure-timing"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-alert :value="showWarning" type="info">
          Please note: changing 'days invoice is due after raising' will not change the due date 
          for any invoices that have already been raised.
        </v-alert>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="configuration.daysInvoiceIsDueAfterGeneratingInvoice"
                  :rules="[rules.required, rules.integer, rules.zeroOrGreater, daysDueRange]"
                  label="Days invoice is due after raising*"
                  @input="update('daysInvoiceIsDueAfterGeneratingInvoice', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="configuration.daysBeforeInvoiceIsDueToSendPaymentDueReminderNotice"
                  :rules="[rules.required, rules.integer, rules.greaterThanZero]"
                  label="Days before invoice is due to send payment due reminder notice*"
                  @input="update('daysBeforeInvoiceIsDueToSendPaymentDueReminderNotice', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="configuration.daysAfterInvoiceIsDueToSendPaymentOverdueWarningNotice"
                  :rules="[rules.required, rules.integer, rules.greaterThanZero]"
                  label="Days after invoice is due to send payment overdue warning notice*"
                  @input="update('daysAfterInvoiceIsDueToSendPaymentOverdueWarningNotice', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="configuration.daysAfterInvoiceIsDueToDisplayWarning"
                  :rules="[rules.required, rules.integer, rules.greaterThanZero]"
                  label="Days after invoice is due to display warnings*"
                  @input="update('daysAfterInvoiceIsDueToDisplayWarning', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="primary" 
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error"/>
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import * as mutation from "@/store/levy-collections/mutation-types";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false,
      daysDueRange: v => (parseInt(v) > 150 ? "Value must be 150 or less" : true)
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.editInvoicingTimingConfiguration;
    },
    configuration() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    showWarning() {
      return this.state.data.status == "Invoicing";
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.dispatch(action.EDIT_INVOICING_TIMING_CONFIGURATION_GET, this.levyCollectionId);
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
